import React, { useState } from 'react';

import type { JsonValue } from '../types';

export interface AddKeyDialogProps {
  existingKeys: string[];
  onCancel: () => void;
  onConfirm: (key: string, value: JsonValue) => void;
}

/**
 * Modal shown when adding a new key to an object: asks for the key name and
 * optionally a JSON value to paste in (object/array/primitive), instead of
 * silently assigning "new_key" with an empty string.
 */
export const AddKeyDialog: React.FC<AddKeyDialogProps> = ({ existingKeys, onCancel, onConfirm }) => {
  const [keyName, setKeyName] = useState('');
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const trimmedKey = keyName.trim();
  const isDuplicate = trimmedKey.length > 0 && existingKeys.includes(trimmedKey);

  const handleConfirm = () => {
    if (!trimmedKey) {
      setError('Key name is required.');
      return;
    }

    if (isDuplicate) {
      setError(`A key named "${trimmedKey}" already exists in this object. Choose a different name.`);
      return;
    }

    let value: JsonValue = '';
    const trimmedJson = jsonText.trim();
    if (trimmedJson) {
      try {
        value = JSON.parse(trimmedJson) as JsonValue;
      } catch (parseError) {
        setError(`Invalid JSON: ${(parseError as Error).message}`);
        return;
      }
    }

    setError(null);
    onConfirm(trimmedKey, value);
  };

  return (
    <div className="sje-modal-overlay" role="dialog" aria-modal="true" aria-label="Add key">
      <div className="sje-modal">
        <div className="sje-modal-header">Add key</div>
        <div className="sje-modal-body">
          <div className="sje-panel-field">
            <label className="sje-panel-label" htmlFor="sje-new-key-name">
              Key name
            </label>
            <input
              id="sje-new-key-name"
              className="sje-panel-input"
              autoFocus
              value={keyName}
              onChange={(event) => setKeyName(event.target.value)}
              placeholder="e.g. new_field"
            />
            {isDuplicate ? (
              <div className="sje-modal-hint sje-modal-hint-danger">
                A key named "{trimmedKey}" already exists in this object. Choose a different name.
              </div>
            ) : null}
          </div>
          <div className="sje-panel-field">
            <label className="sje-panel-label" htmlFor="sje-new-key-json">
              Value (optional — paste a valid JSON value)
            </label>
            <textarea
              id="sje-new-key-json"
              className="sje-panel-input sje-modal-textarea"
              rows={8}
              value={jsonText}
              onChange={(event) => setJsonText(event.target.value)}
              placeholder={'e.g. {"label": "New Field", "visible": true}\nor ["a", "b"]\nor "some text"'}
            />
            <div className="sje-modal-hint">Leave empty to add the key with an empty string value.</div>
          </div>
          {error ? <div className="sje-error-banner">{error}</div> : null}
        </div>
        <div className="sje-modal-footer">
          <button type="button" className="sje-panel-button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="sje-panel-button sje-panel-button-primary"
            onClick={handleConfirm}
            disabled={isDuplicate}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};
